import api from './api';

export interface CreateProductDto {
  title: string;
  description: string;
  price: number;
  category: string;
  sector: string;
  tags: string[];
  fileUrl: string;
  previewUrl?: string;
  thumbnail?: string;
  media?: MediaDto[];
}

export interface MediaDto {
  id?: string;
  key: string;
  fileName: string;
  fileUrl: string;
  signedUrl?: string; // Signed URL for accessing the media
  fileSize: number;
  mimeType: string;
  width?: number;
  height?: number;
  entityType: string;
  description?: string;
}

export interface Review {
  id: string;
  rating: number;
  comment?: string;
  createdAt: string;
  user: {
    id: string;
    name: string;
    avatar?: string;
  };
}

export interface Product {
  id: string;
  title: string;
  description: string;
  price: string;
  category: string;
  sector: string;
  tags: string[];
  fileUrl: string;
  fileSignedUrl?: string; // Signed URL for main file
  previewUrl?: string;
  previewSignedUrl?: string; // Signed URL for preview
  thumbnail?: string;
  thumbnailUrl?: string; // Signed URL for thumbnail
  downloads: number;
  rating: number;
  reviewCount: number;
  status: string;
  sellerId: string;
  seller: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  media: MediaDto[];
  reviews?: Review[];
  _count?: {
    purchases?: number;
    reviews?: number;
  };
  createdAt: string;
  updatedAt: string;
}

export const productService = {
  /**
   * Create a new product
   */
  async createProduct(productData: CreateProductDto): Promise<Product> {
    const response = await api.post('/products', productData);
    return response.data;
  },

  /**
   * Get all products
   */
  async getAllProducts(filters?: {
    category?: string;
    sector?: string;
    minPrice?: number;
    maxPrice?: number;
    search?: string;
    status?: string;
  }): Promise<Product[]> {
    // Filter out undefined values before sending to API
    const params: Record<string, any> = {};
    if (filters) {
      Object.keys(filters).forEach((key) => {
        const value = filters[key as keyof typeof filters];
        if (value !== undefined && value !== null) {
          params[key] = value;
        }
      });
    }
    
    const response = await api.get('/products', {
      params,
    });
    return response.data;
  },

  /**
   * Get my products
   */
  async getMyProducts(): Promise<Product[]> {
    const response = await api.get('/products/seller/my-products');
    return response.data;
  },

  /**
   * Get single product
   */
  async getProduct(id: string): Promise<Product> {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  /**
   * Update product
   */
  async updateProduct(
    id: string,
    productData: Partial<CreateProductDto>
  ): Promise<Product> {
    const response = await api.patch(`/products/${id}`, productData);
    return response.data;
  },

  /**
   * Delete product
   */
  async deleteProduct(id: string): Promise<void> {
    await api.delete(`/products/${id}`);
  },

  /**
   * Increment download count
   */
  async incrementDownloads(id: string): Promise<void> {
    await api.post(`/products/${id}/download`);
  },
};

